/**
 * 誕生日入力サブステップ
 */
import { useState, useRef } from "react";
import dayjs from "dayjs";
import { Select } from "common/elements";
import I18n from "features/shared/i18n";
import { EditorProfileState } from "../types";

interface BirthdayStepProps {
  state: EditorProfileState;
  updateState: (updates: Partial<EditorProfileState>) => void;
}

export const BirthdayStep = ({ state, updateState }: BirthdayStepProps) => {
  // 年の範囲（100年）
  const currentYear = dayjs().year();
  const minYear = currentYear - 100;
  const maxYear = currentYear;

  // ドロップダウン開閉状態
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);

  // ボタンref
  const yearRef = useRef<HTMLButtonElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);

  const selectedYear = state.birthdayYear;
  const selectedMonth = state.birthdayMonth;
  const selectedDay = state.birthdayDay;

  const yearSuffixKey = "MSG_BIRTHDAY_YEAR_SUFFIX_BY_PARENT";
  const monthSuffixKey = "MSG_BIRTHDAY_MONTH_SUFFIX_BY_PARENT";
  const daySuffixKey = "MSG_BIRTHDAY_DAY_SUFFIX_BY_PARENT";

  // 年オプション
  const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
    const year = maxYear - i;
    return { value: year, label: `${year}${I18n.t(yearSuffixKey)}` };
  });

  // 月オプション
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t(monthSuffixKey)}`,
  }));

  // 日オプション（年月に応じて動的に計算）
  const daysInMonth =
    selectedYear && selectedMonth ? dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth() : 31;
  const dayOptions = Array.from({ length: daysInMonth }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t(daySuffixKey)}`,
  }));

  const handleYearChange = (year: number) => {
    const month = selectedMonth;
    const day = selectedDay;
    let validDay = day;
    if (month && day) {
      const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
      validDay = Math.min(day, maxDay);
    }
    if (month && validDay) {
      updateState({
        birthdayYear: year,
        birthdayDay: validDay,
        birthday: dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"),
      });
    } else {
      updateState({
        birthdayYear: year,
        birthday: "",
      });
    }
  };

  const handleMonthChange = (month: number) => {
    const year = selectedYear;
    const day = selectedDay;
    let validDay = day;
    if (year && day) {
      const maxDay = dayjs(`${year}-${month}-01`).daysInMonth();
      validDay = Math.min(day, maxDay);
    }
    if (year && validDay) {
      updateState({
        birthdayMonth: month,
        birthdayDay: validDay,
        birthday: dayjs(`${year}-${month}-${validDay}`).format("YYYY-MM-DD"),
      });
    } else {
      updateState({
        birthdayMonth: month,
        birthday: "",
      });
    }
  };

  const handleDayChange = (day: number) => {
    const year = selectedYear;
    const month = selectedMonth;
    if (year && month) {
      updateState({
        birthdayDay: day,
        birthday: dayjs(`${year}-${month}-${day}`).format("YYYY-MM-DD"),
      });
    } else {
      updateState({
        birthdayDay: day,
        birthday: "",
      });
    }
  };

  const buttonBase =
    "flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[20px] cursor-pointer select-none bg-white border-2 border-gray2-60 text-textcolor-black hover:border-blue-100 transition-all";
  const yearButtonClass = `${buttonBase} w-[160px]`;
  const monthDayButtonClass = `${buttonBase} w-[140px]`;

  return (
    <div className="flex-col-el items-center justify-center w-full h-full">
      {/* タイトル */}
      <div className="flex-col-el flex-center mb-8">
        <p className="text-body text-textcolor-black text-[24px]">
          {I18n.t("MSG_CREATE_USER_TITLE_BIRTHDAY_BY_PARENT")}
        </p>
      </div>

      <div className="flex-row-el flex-center gap-4">
        {/* 年 */}
        <button
          ref={yearRef}
          onClick={() => setYearOpen(true)}
          className={yearButtonClass}
        >
          <span>
            {selectedYear ? `${selectedYear}${I18n.t(yearSuffixKey)}` : I18n.t(yearSuffixKey)}
          </span>
          <span className="material-symbols-outlined !text-[20px]">expand_more</span>
        </button>
        <Select
          anchorRef={yearRef}
          open={yearOpen}
          onClose={() => setYearOpen(false)}
          value={selectedYear}
          onChange={handleYearChange}
          options={yearOptions}
        />

        {/* 月 */}
        <button
          ref={monthRef}
          onClick={() => setMonthOpen(true)}
          className={monthDayButtonClass}
        >
          <span>
            {selectedMonth ? `${selectedMonth}${I18n.t(monthSuffixKey)}` : I18n.t(monthSuffixKey)}
          </span>
          <span className="material-symbols-outlined !text-[20px]">expand_more</span>
        </button>
        <Select
          anchorRef={monthRef}
          open={monthOpen}
          onClose={() => setMonthOpen(false)}
          value={selectedMonth}
          onChange={handleMonthChange}
          options={monthOptions}
        />

        {/* 日 */}
        <button
          ref={dayRef}
          onClick={() => setDayOpen(true)}
          className={monthDayButtonClass}
        >
          <span>
            {selectedDay ? `${selectedDay}${I18n.t(daySuffixKey)}` : I18n.t(daySuffixKey)}
          </span>
          <span className="material-symbols-outlined !text-[20px]">expand_more</span>
        </button>
        <Select
          anchorRef={dayRef}
          open={dayOpen}
          onClose={() => setDayOpen(false)}
          value={selectedDay}
          onChange={handleDayChange}
          options={dayOptions}
        />
      </div>
    </div>
  );
};

/**
 * 誕生日が有効かどうかを判定するヘルパー関数
 */
export const isValidBirthday = (date: string): boolean => {
  if (!date) return false;
  if (date === "0000-00-00" || date.startsWith("0000")) return false;
  const d = dayjs(date);
  if (!d.isValid()) return false;
  if (d.year() < 1920) return false;
  return true;
};
